import {
    Box,
    Flex,
    Skeleton,
    Text,
    VStack,
    HStack,
    useColorModeValue,
    Badge,
} from "@chakra-ui/react";
import Gradient from "javascript-color-gradient";

function Ranking({ stations = [] }) {
    const textPrimary = useColorModeValue("gray.800", "white");
    const textSecondary = useColorModeValue("gray.500", "gray.400");
    const textMuted = useColorModeValue("gray.400", "gray.500");
    const rowBg = useColorModeValue("gray.50", "gray.750");
    const defaultRankBg = useColorModeValue("gray.200", "gray.600");

    function getTempColor(temp) {
        if (isNaN(temp)) return "gray.400";
        temp = Math.round(temp);

        if (temp <= 0) temp = 1;
        if (temp >= 40) temp = 40;

        return new Gradient()
            .setColorGradient("#00b0f0", "#FDEB84", "#F24044")
            .setMidpoint(40)
            .getColor(parseInt(temp));
    }

    // Ranking row component
    const RankingRow = ({ station, index }) => (
        <Flex
            align="center"
            py={2}
            px={3}
            bg={index % 2 === 0 ? "transparent" : rowBg}
            borderRadius="md"
            transition="all 0.15s"
            _hover={{ bg: rowBg }}
        >
            {/* Rank Number */}
            <Box
                w="24px"
                h="24px"
                borderRadius="full"
                bg={index < 3 ? getTempColor(station.temp) : defaultRankBg}
                color={index < 3 ? "white" : textSecondary}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                fontWeight="bold"
                mr={3}
                flexShrink={0}
            >
                {index + 1}
            </Box>

            {/* Station Info */}
            <VStack spacing={0} align="start" flex={1} minW={0}>
                <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={textPrimary}
                    noOfLines={1}
                >
                    {station.city}/{station.state}
                </Text>
                <HStack spacing={2}>
                    <Badge
                        colorScheme="gray"
                        variant="subtle"
                        fontSize="2xs"
                        textTransform="lowercase"
                    >
                        {station.provider}
                    </Badge>
                    <Text fontSize="2xs" color={textMuted}>
                        {station.moment}
                    </Text>
                </HStack>
            </VStack>

            {/* Temperature */}
            <Box
                bg={getTempColor(station.temp)}
                color="white"
                px={3}
                py={1}
                borderRadius="lg"
                fontWeight="bold"
                fontSize="sm"
                textShadow="0 1px 2px rgba(0,0,0,0.2)"
                flexShrink={0}
            >
                {station.temp}°
            </Box>
        </Flex>
    );

    // Loading skeleton
    const SkeletonRow = ({ index }) => (
        <Flex
            align="center"
            py={2}
            px={3}
            bg={index % 2 === 0 ? "transparent" : rowBg}
            borderRadius="md"
        >
            <Skeleton w="24px" h="24px" borderRadius="full" mr={3} />
            <VStack spacing={1} align="start" flex={1}>
                <Skeleton h="14px" w="70%" />
                <Skeleton h="10px" w="40%" />
            </VStack>
            <Skeleton w="50px" h="28px" borderRadius="lg" />
        </Flex>
    );

    return (
        <VStack spacing={1} align="stretch">
            {stations?.length ? (
                stations.map((station, index) => (
                    <RankingRow
                        key={station.identifier || index}
                        station={station}
                        index={index}
                    />
                ))
            ) : (
                [...Array(5).keys()].map(i => (
                    <SkeletonRow key={i} index={i} />
                ))
            )}
        </VStack>
    );
}

export default Ranking;
